import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Play, Pause, RotateCcw, Volume2, VolumeX, Maximize, Activity, X, ChevronRight, ChevronLeft, ClipboardCheck, Monitor, BarChart3 } from 'lucide-react';
import AnimatedDemo from './AnimatedDemo';
import { trackDemoEvent } from './demoAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GUIDED_SCREENS = [
  {
    key: 'preparar', title: 'PREPARAR', icon: ClipboardCheck,
    text: 'Selecciona o configura un escenario clínico.',
    mock: (
      <div className="grid grid-cols-2 gap-2 p-4">
        {['Paro Cardiorrespiratorio Adulto', 'Crisis Asmática Severa', 'IAM con SDST', 'Sepsis de Foco Pulmonar'].map((n, i) => (
          <div key={n} className={`rounded-lg border p-3 text-left ${i === 0 ? 'border-cyan-500 bg-cyan-500/10' : 'border-slate-700 bg-slate-800'}`}>
            <div className="text-white text-xs font-semibold">{n}</div>
            <div className="text-slate-500 text-[10px] mt-1">Objetivos · Vitales iniciales · Eventos programados</div>
          </div>
        ))}
      </div>
    ),
  },
  {
    key: 'simular', title: 'SIMULAR', icon: Monitor,
    text: 'El estudiante toma decisiones mientras el paciente evoluciona.',
    mock: (
      <div className="p-4 space-y-2">
        <div className="grid grid-cols-4 gap-2">
          {[['FC', '118', 'text-emerald-400'], ['PA', '135/85', 'text-cyan-400'], ['SpO2', '86%', 'text-rose-400'], ['FR', '30', 'text-amber-400']].map(([l, v, c]) => (
            <div key={l} className="bg-slate-900 rounded-lg p-2 text-center border border-slate-700">
              <div className={`text-lg font-bold font-mono ${c}`}>{v}</div>
              <div className="text-[9px] text-slate-400">{l}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {['Oxigenoterapia', 'Salbutamol 5 mg NEB', 'Hidrocortisona IV', 'Control de signos'].map(a => (
            <span key={a} className="text-[10px] px-2 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-300">{a}</span>
          ))}
        </div>
      </div>
    ),
  },
  {
    key: 'evaluar', title: 'EVALUAR', icon: BarChart3,
    text: 'El instructor analiza acciones, resultados y oportunidades de mejora.',
    mock: (
      <div className="p-4 space-y-2">
        <div className="text-3xl font-bold text-white">87<span className="text-base text-slate-400">/100</span></div>
        {[['Manejo clínico', 92], ['Tiempos de respuesta', 84], ['Comunicación', 78]].map(([l, v]) => (
          <div key={l}>
            <div className="flex justify-between text-[10px] text-slate-300 mb-0.5"><span>{l}</span><span>{v}%</span></div>
            <div className="h-1.5 rounded bg-slate-700"><div className="h-full rounded bg-gradient-to-r from-cyan-500 to-blue-500" style={{ width: `${v}%` }} /></div>
          </div>
        ))}
      </div>
    ),
  },
];

function GuidedDemoModal({ open, onClose, onRequestDemo }) {
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open]);
  if (!open) return null;
  const s = GUIDED_SCREENS[step];
  const Icon = s.icon;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Demostración guiada" data-testid="guided-demo-modal">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-cyan-400" />
            <span className="text-white font-bold text-sm tracking-widest">{s.title}</span>
            <span className="text-slate-500 text-xs">({step + 1}/3)</span>
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="text-slate-400 hover:text-white" data-testid="close-guided-demo-btn"><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-slate-900/60 min-h-[190px]" data-testid={`guided-screen-${s.key}`}>{s.mock}</div>
        <div className="p-4">
          <p className="text-slate-300 text-sm mb-4">{s.text}</p>
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
              className="flex items-center gap-1 text-slate-400 hover:text-white text-sm disabled:opacity-30" data-testid="guided-prev-btn">
              <ChevronLeft className="w-4 h-4" /> Anterior
            </button>
            {step < 2 ? (
              <button onClick={() => setStep(step + 1)}
                className="flex items-center gap-1 bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="guided-next-btn">
                Siguiente <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={() => { onClose(); onRequestDemo(); }}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="guided-cta-btn">
                Solicitar demostración completa
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CommercialDemoSection({ onRequestDemo, standMode = false }) {
  const [config, setConfig] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [ended, setEnded] = useState(false);
  const [started, setStarted] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [guidedOpen, setGuidedOpen] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const sectionRef = useRef(null);
  const milestones = useRef(new Set());

  useEffect(() => {
    axios.get(`${API_URL}/api/public/landing-demo`).then(r => setConfig(r.data)).catch(() => setConfig({ activo: true, autoplay: true, loop: false }));
  }, []);

  const hasVideo = !!config?.video_url;
  const loop = standMode ? true : !!config?.loop;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const videoSrc = isMobile && config?.video_mobile_url ? config.video_mobile_url : config?.video_url;

  const checkMilestones = useCallback((current, duration) => {
    if (!duration) return;
    const pct = (current / duration) * 100;
    [[25, 'demo_video_25'], [50, 'demo_video_50'], [75, 'demo_video_75']].forEach(([p, ev]) => {
      if (pct >= p && !milestones.current.has(ev)) { milestones.current.add(ev); trackDemoEvent(ev); }
    });
  }, []);

  const play = useCallback(() => {
    setEnded(false);
    setStarted(true);
    trackDemoEvent('demo_video_view');
    if (hasVideo && videoRef.current) videoRef.current.play().catch(() => {});
    setPlaying(true);
  }, [hasVideo]);

  const pause = useCallback(() => {
    if (hasVideo && videoRef.current) videoRef.current.pause();
    setPlaying(false);
  }, [hasVideo]);

  const restart = () => {
    setEnded(false);
    milestones.current.clear();
    if (hasVideo && videoRef.current) { videoRef.current.currentTime = 0; videoRef.current.play().catch(() => {}); }
    else setResetKey(k => k + 1);
    setPlaying(true);
  };

  const handleEnded = useCallback(() => {
    trackDemoEvent('demo_video_complete');
    setPlaying(false);
    setEnded(true);
  }, []);

  // Autoplay muted al entrar en viewport
  useEffect(() => {
    if (!config?.autoplay && !standMode) return;
    if (!sectionRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting && !started) {
          setMuted(!(config?.sonido_inicial && standMode));
          play();
        }
      });
    }, { threshold: 0.4 });
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, [config, started, play, standMode]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  const fullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  if (config && config.activo === false && !standMode) return null;

  const controls = (
    <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 z-20">
      <div className="flex items-center gap-1.5 bg-black/60 backdrop-blur rounded-lg p-1">
        <button onClick={playing ? pause : play} aria-label={playing ? 'Pausar' : 'Reproducir'}
          className="text-white hover:text-cyan-400 p-1.5" data-testid="demo-play-pause-btn">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button onClick={restart} aria-label="Reiniciar" className="text-white hover:text-cyan-400 p-1.5" data-testid="demo-restart-btn">
          <RotateCcw className="w-4 h-4" />
        </button>
        {hasVideo && (
          <button onClick={() => setMuted(!muted)} aria-label={muted ? 'Activar sonido' : 'Silenciar'}
            className="text-white hover:text-cyan-400 p-1.5" data-testid="demo-mute-btn">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        )}
        <button onClick={fullscreen} aria-label="Pantalla completa" className="text-white hover:text-cyan-400 p-1.5" data-testid="demo-fullscreen-btn">
          <Maximize className="w-4 h-4" />
        </button>
      </div>
      {hasVideo && muted && started && !ended && (
        <button onClick={() => setMuted(false)}
          className="bg-cyan-500/90 hover:bg-cyan-400 text-white text-xs font-semibold px-3 py-1.5 rounded-lg backdrop-blur"
          data-testid="demo-sound-btn">
          Ver con sonido
        </button>
      )}
    </div>
  );

  const posterOverlay = !started && (
    <button onClick={play} aria-label="Reproducir demostración"
      className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-blue-950/95 group"
      style={config?.poster_url ? { backgroundImage: `url(${config.poster_url.startsWith('http') ? config.poster_url : API_URL + config.poster_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
      data-testid="demo-poster">
      <div className="absolute inset-0 bg-slate-950/60" />
      <div className="relative flex flex-col items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-bold text-lg">Simu<span className="text-cyan-400">Clinic</span></span>
        </div>
        <div className="w-16 h-16 rounded-full bg-cyan-500 group-hover:bg-cyan-400 group-hover:scale-110 flex items-center justify-center shadow-2xl shadow-cyan-500/50 transition-all">
          <Play className="w-7 h-7 text-white ml-1" />
        </div>
        <p className="text-white text-sm font-medium">Descubre SimuClinic en 30 segundos</p>
      </div>
    </button>
  );

  const endOverlay = ended && !standMode && (
    <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-slate-950/90 p-4 text-center" data-testid="demo-end-cta">
      <p className="text-white text-base md:text-xl font-bold max-w-md">¿Quieres ver SimuClinic funcionando con tu institución?</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={() => { trackDemoEvent('demo_contact_click', false); onRequestDemo(); }}
          className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold px-6 py-3 rounded-xl shadow-lg shadow-cyan-500/30"
          data-testid="demo-end-contact-btn">
          Solicitar una demostración
        </button>
        <button onClick={() => { trackDemoEvent('demo_plans_click', false); document.getElementById('planes')?.scrollIntoView({ behavior: 'smooth' }); }}
          className="bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-xl border border-white/20"
          data-testid="demo-end-plans-btn">
          Ver planes
        </button>
      </div>
      <button onClick={restart} className="text-slate-400 hover:text-white text-xs flex items-center gap-1">
        <RotateCcw className="w-3 h-3" /> Ver de nuevo
      </button>
    </div>
  );

  const player = (
    <div ref={containerRef} className="relative aspect-video bg-slate-950 rounded-lg overflow-hidden" data-testid="demo-player">
      {hasVideo ? (
        <video ref={videoRef} className="w-full h-full object-contain bg-black"
          preload="metadata" playsInline muted={muted} loop={loop}
          poster={config?.poster_url ? (config.poster_url.startsWith('http') ? config.poster_url : API_URL + config.poster_url) : undefined}
          onTimeUpdate={(e) => checkMilestones(e.target.currentTime, e.target.duration)}
          onEnded={handleEnded}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          aria-label="Video demostrativo de SimuClinic"
          data-testid="demo-video">
          {config?.video_webm_url && <source src={config.video_webm_url.startsWith('http') ? config.video_webm_url : API_URL + config.video_webm_url} type="video/webm" />}
          <source src={videoSrc?.startsWith('http') ? videoSrc : API_URL + videoSrc} type="video/mp4" />
        </video>
      ) : (
        <AnimatedDemo playing={playing} loop={loop} resetKey={resetKey}
          onTime={checkMilestones} onEnded={handleEnded} />
      )}
      {posterOverlay}
      {started && controls}
      {endOverlay}
    </div>
  );

  if (standMode) {
    return <div className="w-full h-full" ref={sectionRef}>{player}</div>;
  }

  return (
    <section id="demo" ref={sectionRef} className="py-20 bg-slate-900 relative overflow-hidden" data-testid="commercial-demo-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/10 via-transparent to-transparent pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="demo-section-title">
            {config?.titulo || 'Conoce SimuClinic en 30 segundos'}
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto">
            {config?.subtitulo || 'Descubre cómo una simulación pasa desde la preparación del escenario hasta la evaluación y el debriefing.'}
          </p>
        </motion.div>

        {/* Mockup notebook (desktop) / full-width (móvil) */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="max-w-4xl mx-auto">
            <div className="rounded-xl md:rounded-t-2xl md:rounded-b-none overflow-hidden border border-slate-700 md:border-b-0 md:bg-slate-800 md:p-3">
              <div className="hidden md:flex gap-1.5 mb-2 px-1">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
              </div>
              {player}
            </div>
            <div className="hidden md:block h-4 bg-slate-700 rounded-b-2xl mx-[-2%] shadow-xl" />
          </div>
        </motion.div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
          <button onClick={() => { setEnded(false); play(); sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-cyan-500/30 transition-all"
            data-testid="demo-watch-btn">
            <Play className="w-5 h-5" /> Ver demostración
          </button>
          <button onClick={() => { trackDemoEvent('demo_contact_click', false); onRequestDemo(); }}
            className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 rounded-xl border border-white/20 transition-all"
            data-testid="demo-request-btn">
            {config?.boton_principal || 'Solicitar demo personalizada'}
          </button>
          <button onClick={() => { trackDemoEvent('demo_guided_open', false); setGuidedOpen(true); }}
            className="flex items-center justify-center gap-2 text-cyan-400 hover:text-cyan-300 font-semibold px-4 py-4 transition-colors"
            data-testid="demo-guided-btn">
            {config?.boton_secundario || 'Explorar funcionamiento'} <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <p className="text-center mt-4">
          <a href="/demo-stand" target="_blank" rel="noopener noreferrer"
            className="text-slate-400 hover:text-cyan-300 text-sm underline underline-offset-4 transition-colors"
            data-testid="extended-demo-link">
            Ver la versión extendida (2 minutos) con narración
          </a>
        </p>
      </div>

      <GuidedDemoModal open={guidedOpen} onClose={() => setGuidedOpen(false)} onRequestDemo={onRequestDemo} />
    </section>
  );
}
